import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban, Sparkles, BookOpen, Layers, AppWindow, Clock, Zap, Pause,
  Check, AlertTriangle, Minus, ChevronRight, ChevronLeft, Coffee, FileText, Play,
} from "lucide-react";
import { DASHBOARD_BASE } from "../../../../../config/featureFlags";
import { Avatar } from "../../../components/primitives";
import { DashAntd } from "../../../components/DashAntd";
import { fmtMins } from "../../../components/classic/micro";
import { WorkLogEmpty, LogDoor, Prose, LiveStretchFacts } from "../../../components/drilldown/WorkLog";
import { MemberInProject } from "../../../components/drilldown/MemberInProject";
import { GPanel, PanelHead } from "../parts";
import { InfoDot } from "../../../components/classic/InfoDot";
import { inks, paper, foldTools } from "../viz";
import { DrillFrame } from "./DrillFrame";
import { SkeletonRows } from "./StoryChrome";
import { ToolIcon, brandColorFor } from "../../../components/classic/panels";
import { toolGlyphIdFor } from "../../../components/classic/toolIcons";
import { TimeRail, railFromStretches } from "./TimelineKit";

/* Project identity travels as a theme ink name through segment, row dot and
   key — the instrument resolves the actual colour via inks(t) itself. */
const INK_CYCLE = ["blue", "violet", "gold", "pink", "teal"];

function workSummaryInsightsTo(userId, day) {
  const qs = new URLSearchParams();
  if (userId) qs.set("userId", String(userId));
  if (day) qs.set("date", day);
  qs.set("tab", "insights");
  return `${DASHBOARD_BASE}/work-summary-new?${qs}`;
}

function isoDay(raw) {
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = raw instanceof Date ? raw : new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Paint a stretch clock in the viewer's locale when the API still labelled
 *  the day UTC (org TZ change hadn't reached the worklog cache). Prefer
 *  startMs + org offset once the server sends them. */
function hmInView(st, field, timezone) {
  const raw = st?.[field];
  const ms = Number(field === "t1" ? st.endMs : st.startMs);
  const off = Number(st?.offsetMin);
  if (Number.isFinite(ms) && Number.isFinite(off)) {
    const d = new Date(ms + off * 60000);
    return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  }
  const tz = String(timezone || "");
  if ((tz === "UTC" || tz === "GMT" || !tz) && st?.dateLocal && raw) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(raw));
    if (m) {
      const inst = Date.parse(`${st.dateLocal}T${String(m[1]).padStart(2, "0")}:${m[2]}:00.000Z`);
      if (Number.isFinite(inst)) {
        const d = new Date(inst);
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      }
    }
  }
  return raw || "";
}

/** "09:40" → 580 (minutes since midnight). Anything else → null. */
function parseHM(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || "").trim());
  if (!m) return null;
  const v = Number(m[1]) * 60 + Number(m[2]);
  return Number.isFinite(v) ? v : null;
}

/** Quiet section head — ink title, muted icon, hairline. Tone stays a hint
 *  on the tile only so Day / Hours / Projects stay distinct without a rainbow. */
function SectionBar({ t, tone, icon, title, bits, info }) {
  const ink = inks(t)[tone] || inks(t).blue;
  const ppr = paper(t);
  const band = `color-mix(in srgb, ${ink} ${t.isDark ? "20%" : "11%"}, ${ppr.card})`;
  return (
    <div style={{
      margin: "-17px -18px 14px",
      padding: "12px 16px",
      background: band,
      borderBottom: `1px solid ${t.c.border}`,
      borderRadius: `${t.r.card} ${t.r.card} 0 0`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span aria-hidden="true" style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          display: "grid", placeItems: "center",
          background: ink, color: "#fff",
        }}>{icon}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{
            margin: 0, fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px", color: t.c.text,
          }}>{title}</h3>
          {bits?.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 3 }}>
              {bits.map((b) => (
                <span key={b.label} style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  fontSize: 12, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums",
                }}>
                  <span style={{ display: "grid" }}>{b.icon}</span>
                  {b.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {info ? <InfoDot metric={info} tone="graphical" /> : null}
      </div>
    </div>
  );
}

function IcoTile({ color, children, size = 32 }) {
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: 9, flexShrink: 0,
      display: "grid", placeItems: "center",
      background: `${color}22`, color,
    }}>{children}</span>
  );
}

function ProjectRows({ t, projects, inkFor, onOpen }) {
  const k = inks(t);
  if (!projects?.length) return null;
  return (
    <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {projects.map((p) => {
        const ink = k[inkFor(p.name)] || k.blue;
        return (
          <button
            key={p.projectId}
            type="button"
            className="tgw-row"
            onClick={onOpen ? () => onOpen(p) : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              textAlign: "left", border: 0, padding: "8px 6px", borderRadius: 9,
              cursor: onOpen ? "pointer" : "default", fontFamily: t.font.family, color: "inherit",
              background: "transparent",
            }}
          >
            <IcoTile color={ink} size={28}><FolderKanban size={14} strokeWidth={2.2} /></IcoTile>
            <span style={{
              flex: 1, minWidth: 0, fontSize: 13, fontWeight: 550,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{p.name}</span>
            <span style={{
              fontSize: 12, fontVariantNumeric: "tabular-nums", color: t.c.textSecondary, flexShrink: 0,
            }}>{fmtMins(p.trackedMinutes)}</span>
            {onOpen ? <span style={{ color: k.blue, display: "grid" }}><ChevronRight size={15} /></span> : null}
          </button>
        );
      })}
    </div>
  );
}


/* ── the mock's two rows, as components (2026-08-30) ─────────────────────
   "Where the hours went": one row per tool — icon · name · what · inked share
   bar · time. "Chapters of the day": one row per stretch — tool icon · title ·
   when · apps · verdict pill. Both read the SAME worklog the page already
   fetched; nothing new is requested. Verdicts are arithmetic:
     unclear (no write-up)      → "not yet analysed" (gold)
     intensity high             → "Settled" (green)
     intensity low              → "Worth a look" (gold)
     otherwise                  → "Settled" (green) */
const knownTool = (t, name) => Boolean(name && (toolGlyphIdFor(name) || brandColorFor(name, t)));

function VerdictPill({ t, kind }) {
  const k = inks(t);
  const map = {
    look: { c: (t.status && t.status.warning) || k.gold, l: "Worth a look", ico: AlertTriangle },
    ok: { c: (t.status && t.status.positive) || k.teal, l: "Settled", ico: Check },
    na: { c: k.gold, l: "not yet analysed", ico: AlertTriangle },
    thin: { c: t.c.textTertiary, l: "Thin", ico: Minus },
  };
  const m = map[kind] || map.ok;
  const Ico = m.ico;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
      fontSize: 11, fontWeight: 550, padding: "3px 8px", borderRadius: 99,
      background: `${m.c}1f`, color: m.c, flexShrink: 0,
    }}><Ico size={11} strokeWidth={2.4} />{m.l}</span>
  );
}

function stretchVerdict(st) {
  if (st.unclear) return "na";
  const mins = Number(st.minutes) || (() => { const a = parseHM(st.t0), b = parseHM(st.t1); return a != null && b != null && b > a ? b - a : 0; })();
  if (mins > 0 && mins < 15) return "thin";
  if (st.intensity === "low") return "look";
  return "ok";
}

function HoursWent({ t, apps, totalMinutes }) {
  const k = inks(t);
  const ppr = paper(t);
  const cycle = [k.blue, k.violet, k.gold, k.pink, k.teal];
  const list = foldTools(apps).slice(0, 8);
  if (!list.length) return null;
  const max = Math.max(1, ...list.map((a) => a.minutes || 0));
  return (
    <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {list.map((a, i) => {
        const ink = brandColorFor(a.name, t) || cycle[i % cycle.length];
        const share = totalMinutes ? Math.round(((a.minutes || 0) / totalMinutes) * 100) : (a.share || 0);
        return (
          <div key={a.name} className="tgw-row" title={`${a.name} · ${share}% of the day`} style={{
            display: "grid", gridTemplateColumns: "32px 1fr 56px", gap: 10, alignItems: "center",
            padding: "7px 2px", borderRadius: 10,
          }}>
            <ToolIcon name={a.name} type={a.type} size={30} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13, fontWeight: 550, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
              <span style={{ height: 6, marginTop: 4, borderRadius: 99, background: ppr.well, overflow: "hidden", display: "block" }}>
                <i className="tgw-growX" style={{ display: "block", height: "100%", borderRadius: 99, width: `${Math.max(4, ((a.minutes || 0) / max) * 100)}%`, background: ink }} />
              </span>
            </span>
            <b style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", textAlign: "right", color: t.c.textSecondary, fontWeight: 500 }}>{fmtMins(a.minutes || 0)}</b>
          </div>
        );
      })}
    </div>
  );
}

/** Strip "and 8 more pages" so ChatGPT sittings share a name. */
function leadTitle(title) {
  return String(title || "").replace(/\s+and\s+\d+\s+more\s+pages?$/i, "").trim();
}

/** Same tool = one chapter. Chrome/Edge/Firefox collapse by family so the
 *  browser is not listed six times; expand the row for each sitting. */
function stretchApp(st) {
  const raw = String(st.app || "").trim();
  if (raw) return raw;
  const labels = (st.activities || []).map((a) => a && a.label).filter(Boolean);
  return labels.find((l) => toolGlyphIdFor(l)) || labels[0] || "";
}

function chapterKey(st) {
  const app = stretchApp(st).toLowerCase();
  if (app) {
    if (/chrome|chromium/.test(app)) return "a:chrome";
    if (/\bedge\b|msedge/.test(app)) return "a:edge";
    if (/firefox/.test(app)) return "a:firefox";
    if (/safari/.test(app)) return "a:safari";
    return `a:${app}`;
  }
  const title = leadTitle(st.title).toLowerCase();
  return title ? `t:${title}` : "a:unnamed";
}

function chapterHeadline(sittings) {
  const mins = new Map();
  sittings.forEach(({ st }) => {
    const n = leadTitle(st.title) || st.app || "Untitled";
    mins.set(n, (mins.get(n) || 0) + stretchMinutes(st));
  });
  const ranked = [...mins.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);
  if (ranked.length <= 1) return ranked[0] || "Couldn’t be named";
  if (ranked.length === 2) return `${ranked[0]} and ${ranked[1]}`;
  return `${ranked[0]}, ${ranked[1]} +${ranked.length - 2}`;
}

function stretchMinutes(st) {
  const m = Number(st.minutes);
  if (Number.isFinite(m) && m > 0) return m;
  const a = parseHM(st.t0), b = parseHM(st.t1);
  return a != null && b != null && b > a ? b - a : 0;
}
export function groupChapters(stretches, timezone) {
  const order = [];
  const byKey = new Map();
  (stretches || []).forEach((st, i) => {
    const key = chapterKey(st);
    if (!byKey.has(key)) { byKey.set(key, { key, title: st.title, first: st, sittings: [] }); order.push(key); }
    byKey.get(key).sittings.push({ st, i });
  });
  return order.map((key) => {
    const g = byKey.get(key);
    const mins = g.sittings.reduce((acc, x) => acc + stretchMinutes(x.st), 0);
    const verdicts = g.sittings.map((x) => stretchVerdict(x.st));
    const verdict = verdicts.includes("look") ? "look" : verdicts.every((v) => v === "na") ? "na" : verdicts.includes("ok") ? "ok" : verdicts[0];
    const t0 = hmInView(g.sittings[0].st, "t0", timezone);
    const t1 = hmInView(g.sittings[g.sittings.length - 1].st, "t1", timezone);
    const headline = chapterHeadline(g.sittings);
    const app = stretchApp(g.first) || g.sittings.map((x) => stretchApp(x.st)).find(Boolean) || null;
    return { ...g, title: headline, app, mins, verdict, t0, t1, analysed: g.sittings.filter((x) => !x.st.unclear).length };
  });
}

function RailNode({ t, first, last, open, child }) {
  const k = inks(t);
  const ink = child ? t.c.borderStrong : k.blue;
  const size = child ? 8 : 12;
  return (
    <span aria-hidden="true" style={{
      position: "relative", width: 20, alignSelf: "stretch", flexShrink: 0,
    }}>
      <i style={{
        position: "absolute", left: 8, width: 4, background: k.blue,
        top: first ? "50%" : 0, bottom: last ? "50%" : 0, borderRadius: 99,
      }} />
      <i style={{
        position: "absolute", left: 10 - size / 2, top: "50%", marginTop: -size / 2,
        width: size, height: size, borderRadius: 99, background: open ? k.blue : ink,
        boxShadow: child ? "none" : `0 0 0 3px ${paper(t).card}`,
      }} />
    </span>
  );
}

function Chapters({ t, stretches, onOpen, timezone }) {
  const k = inks(t);
  const ppr = paper(t);
  const groups = React.useMemo(() => groupChapters(stretches, timezone), [stretches, timezone]);
  const [open, setOpen] = React.useState(() => new Set());
  if (!groups.length) return null;
  const maxMins = Math.max(1, ...groups.map((g) => g.mins || 0));
  const toggle = (key) => setOpen((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const appsOf = (list) => [...new Set(list.flatMap((x) => (x.st.activities || []).map((a) => a.label)).filter((l) => knownTool(t, l)))].slice(0, 3);
  return (
    <div className="tgw-tl" style={{ display: "flex", flexDirection: "column", padding: "4px 0 8px" }}>
      {groups.map((g, gi) => {
        const st = g.first;
        const many = g.sittings.length > 1;
        const isOpen = open.has(g.key);
        const last = gi === groups.length - 1 && !isOpen;
        const app = (knownTool(t, g.app) && g.app)
          || appsOf(g.sittings)[0]
          || (knownTool(t, st.app) ? st.app : null);
        const share = Math.max(10, Math.round(((g.mins || 0) / maxMins) * 100));
        return (
          <div key={g.key}>
            <button
              type="button" className="tgw-row"
              onClick={many ? () => toggle(g.key) : (onOpen ? () => onOpen(g.sittings[0].i) : undefined)}
              title={g.t0 && g.t1 ? `${g.t0}–${g.t1}` : undefined}
              style={{
                display: "flex", gap: 12, alignItems: "stretch", width: "100%",
                textAlign: "left", padding: "0 4px", cursor: "pointer", border: 0,
                background: "transparent", fontFamily: "inherit", color: "inherit",
                minHeight: 64,
              }}
            >
              <span style={{
                width: 44, flexShrink: 0, display: "flex", flexDirection: "column",
                justifyContent: "center", alignItems: "flex-end", paddingRight: 2,
                fontVariantNumeric: "tabular-nums",
              }}>
                <span style={{ fontSize: 12.5, fontWeight: 650, color: t.c.text }}>{g.t0 || "—"}</span>
                {g.t1 ? <span style={{ fontSize: 11, color: t.c.textTertiary, marginTop: 1 }}>{g.t1}</span> : null}
              </span>
              <RailNode t={t} first={gi === 0} last={last} open={isOpen} />
              <span className="tgw-tl-card" style={{
                flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10,
                margin: "6px 0", padding: "10px 12px", borderRadius: 12,
                background: isOpen ? ppr.well : ppr.card,
                border: `1px solid ${isOpen ? k.blue : t.c.border}`,
                boxShadow: isOpen ? `inset 3px 0 0 ${k.blue}` : "none",
              }}>
                {app ? <ToolIcon name={app} size={28} /> : (
                  <IcoTile color={k.blue} size={28}><Layers size={14} strokeWidth={2.2} /></IcoTile>
                )}
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                    <span style={{
                      fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.2px",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{g.title || st.title || "Couldn’t be named"}</span>
                    {many ? (
                      <span style={{
                        flexShrink: 0, fontSize: 11, fontWeight: 650, color: t.c.textSecondary,
                        background: ppr.well, borderRadius: 999, padding: "1px 7px",
                        fontVariantNumeric: "tabular-nums",
                      }}>×{g.sittings.length}</span>
                    ) : null}
                    {g.verdict === "look" || g.verdict === "thin" ? <VerdictPill t={t} kind={g.verdict} /> : null}
                  </span>
                  <span style={{
                    display: "block", marginTop: 6, height: 5, borderRadius: 99,
                    background: ppr.well, overflow: "hidden",
                  }}>
                    <i className="tgw-growX" style={{
                      display: "block", height: "100%", borderRadius: 99, width: `${share}%`, background: k.blue,
                    }} />
                  </span>
                </span>
                <span style={{
                  fontSize: 13, fontWeight: 650, fontVariantNumeric: "tabular-nums", flexShrink: 0,
                }}>{g.mins ? fmtMins(g.mins) : st.durationLabel}</span>
                <span aria-hidden="true" style={{
                  color: t.c.textTertiary, display: "grid", flexShrink: 0,
                  transform: many && isOpen ? "rotate(90deg)" : "none",
                  transition: "transform .18s",
                }}><ChevronRight size={16} /></span>
              </span>
            </button>
            {many && isOpen ? (
              <div className="tgw-alerts-scroll" style={{ maxHeight: 220 }}>
            {g.sittings.map(({ st: sit, i }, si) => (
              <button key={sit.id || i} type="button" className="tgw-row"
                onClick={onOpen ? () => onOpen(i) : undefined}
                style={{
                  display: "flex", gap: 12, alignItems: "stretch", width: "100%",
                  textAlign: "left", padding: "0 4px", minHeight: 48, border: 0,
                  background: "transparent", cursor: onOpen ? "pointer" : "default",
                  fontFamily: "inherit", color: "inherit",
                }}>
                <span style={{
                  width: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end",
                  fontSize: 11.5, fontVariantNumeric: "tabular-nums", color: t.c.textTertiary,
                }}>{hmInView(sit, "t0", timezone)}</span>
                <RailNode t={t} first={false} last={si === g.sittings.length - 1 && gi === groups.length - 1} child />
                <span className="tgw-tl-card" style={{
                  flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10,
                  margin: "4px 0 4px 10px", padding: "8px 12px", borderRadius: 10,
                  background: ppr.well, border: `1px solid ${t.c.border}`,
                }}>
                  <span style={{
                    flex: 1, minWidth: 0, fontSize: 13, fontWeight: 550,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{leadTitle(sit.title) || sit.app || "Untitled"}</span>
                  <span style={{
                    fontSize: 12, fontVariantNumeric: "tabular-nums", color: t.c.textSecondary, flexShrink: 0,
                  }}>{stretchMinutes(sit) ? fmtMins(stretchMinutes(sit)) : sit.durationLabel}</span>
                  <span style={{ color: k.blue, display: "grid" }}><ChevronRight size={14} /></span>
                </span>
              </button>
            ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function StretchSolid({ t, stretch, index, total, onJump, onFullLog, userId }) {
  const k = inks(t);
  const ppr = paper(t);
  const s = stretch;
  const acts = (s.activities || []).filter((a) => a && a.label);
  const totalWeight = acts.reduce((a, x) => a + (Number(x.weight) || 0), 0) || 1;
  const live = s.source === "live";
  const clock = (() => {
    if (!s.t0) return null;
    const hm = (v) => `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;
    const a = Number(s.t0.slice(0, 2)) * 60 + Number(s.t0.slice(3, 5));
    const b = s.t1 ? Number(s.t1.slice(0, 2)) * 60 + Number(s.t1.slice(3, 5)) : a + 30;
    if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
    const pad = 90;
    const win0 = Math.max(0, a - pad);
    const win1 = Math.min(1440, b + pad);
    const span = Math.max(1, win1 - win0);
    return {
      left: ((a - win0) / span) * 100,
      width: Math.max(10, ((b - a) / span) * 100),
      from: hm(win0),
      to: hm(win1),
      mins: b - a,
    };
  })();
  const NavBtn = ({ disabled, onClick, children }) => (
    <button type="button" disabled={disabled} onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
      borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
      color: disabled ? t.c.textTertiary : t.c.text, cursor: disabled ? "default" : "pointer",
      fontFamily: "inherit", fontSize: 12.5, fontWeight: 650, opacity: disabled ? 0.45 : 1,
    }}>{children}</button>
  );
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 13, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
          Sitting {Number.isFinite(index) ? index + 1 : "—"} of {total || "—"}
        </span>
        <span style={{ flex: 1 }} />
        <NavBtn disabled={index <= 0} onClick={() => onJump(index - 1)}><ChevronLeft size={14} /> Prev</NavBtn>
        <NavBtn disabled={index >= total - 1} onClick={() => onJump(index + 1)}>Next <ChevronRight size={14} /></NavBtn>
      </div>
      <div className="tgw-pair tgw-a tgw-a2" style={{
        display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "start",
      }}>
        <GPanel t={t} style={{ margin: 0, background: t.isDark ? ppr.card : "#F7F9FC" }}>
          <SectionBar t={t} tone="violet" icon={<FileText size={16} strokeWidth={2.2} />} title="What happened" info="attention"
            bits={[
              { icon: <AppWindow size={12} strokeWidth={2.2} />, label: acts.length ? `${acts.length} tool${acts.length === 1 ? "" : "s"}` : "no tools named" },
              { icon: s.unclear ? <AlertTriangle size={12} strokeWidth={2.2} /> : <Check size={12} strokeWidth={2.2} />, label: s.unclear ? "not yet analysed" : "written up" },
            ]} />
          {s.summary ? (
            <Prose text={s.summary} numbered activities={acts} />
          ) : live ? (
            <LiveStretchFacts t={t} s={s} userId={userId} embedded />
          ) : (
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: t.c.textSecondary }}>
              Nothing in this sitting was distinctive enough to name. The time is real; the write-up is honestly absent.
            </p>
          )}
          {!live && acts.length > 0 ? (
            <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 16 }}>
              {acts.map((a, i) => {
                const pct = Number(a.weight) > 0 ? Math.round((a.weight / totalWeight) * 100) : null;
                return (
                  <div key={i} className="tgw-row" style={{
                    display: "grid", gridTemplateColumns: "32px 1fr 48px", gap: 10, alignItems: "center",
                    padding: "8px 4px", borderRadius: 10,
                  }}>
                    <ToolIcon name={a.label} type={a.type} size={30} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 550, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.label}</span>
                      {a.detail ? <span style={{ display: "block", fontSize: 12, color: t.c.textTertiary, marginTop: 2 }}>{a.detail}</span> : null}
                      {pct != null ? (
                        <span style={{ display: "block", height: 5, marginTop: 5, borderRadius: 99, background: ppr.well, overflow: "hidden" }}>
                          <i style={{ display: "block", height: "100%", width: `${pct}%`, borderRadius: 99, background: k.blue }} />
                        </span>
                      ) : null}
                    </span>
                    <b style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", textAlign: "right", color: t.c.textSecondary, fontWeight: 500 }}>{pct != null ? `${pct}%` : ""}</b>
                  </div>
                );
              })}
            </div>
          ) : null}
        </GPanel>
        <aside>
          <GPanel t={t} style={{ marginBottom: 12 }}>
            <SectionBar t={t} tone="blue" icon={<Clock size={16} strokeWidth={2.2} />} title="On the clock" info="rhythm"
              bits={[{ icon: <Clock size={12} strokeWidth={2.2} />, label: clock ? `${clock.mins} min` : "no clock times" }]} />
            {clock ? (
              <div>
                <div style={{
                  padding: "12px 14px", borderRadius: 12, background: ppr.well, marginBottom: 12,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 650, color: t.c.textSecondary }}>This sitting</div>
                  <div style={{ marginTop: 2, fontSize: 26, fontWeight: 750, letterSpacing: "-0.8px", fontVariantNumeric: "tabular-nums" }}>
                    {s.durationLabel || fmtMins(clock.mins)}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 14, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>
                    {s.t0} → {s.t1}
                  </div>
                </div>
                <div style={{ position: "relative", height: 12, borderRadius: 99, background: ppr.well, overflow: "hidden" }}>
                  <i style={{
                    position: "absolute", top: 0, bottom: 0, left: `${clock.left}%`, width: `${clock.width}%`,
                    borderRadius: 99, background: k.blue,
                  }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: t.c.textTertiary, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                  <span>{clock.from}</span>
                  <span>{clock.to}</span>
                </div>
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: t.c.textTertiary }}>This sitting has no clock times, so it cannot sit on the day.</p>
            )}
          </GPanel>
          <button type="button" onClick={onFullLog} style={{
            display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
            padding: "15px 14px", borderRadius: 14, cursor: "pointer",
            fontFamily: "inherit", color: "#fff", border: 0, background: k.blue,
            boxShadow: `0 8px 20px -10px ${k.blue}`,
          }}>
            <span aria-hidden="true" style={{
              width: 40, height: 40, borderRadius: 11, flexShrink: 0,
              display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)",
            }}><BookOpen size={18} strokeWidth={2.2} /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14, fontWeight: 650, letterSpacing: "-0.2px" }}>Work summary · insights</span>
              <span style={{ display: "block", fontSize: 12.5, opacity: 0.82, marginTop: 2 }}>
                {total ? `That day · ${total} sittings` : "That person’s day"}
              </span>
            </span>
            <span style={{ display: "grid" }}><ChevronRight size={16} /></span>
          </button>
        </aside>
      </div>
    </>
  );
}

export function GraphicalPerson({
  t, view, name, position, periodLabel, userId, range,
  dossier, m, loading, error,
  worklog, worklogLoading, hasWork, stretches, stretch, focusIndex,
  projectId, projectName, projectRow,
  crumbs, layers, backTo, backLabel, subject, excludes,
  openTakeaway, openLog, openProject, onBack,
}) {
  const navigate = useNavigate();
  const layer = view === "log" ? 3 : (view === "takeaway" || view === "project") ? 2 : 1;
  const k = inks(t);
  const insightsDay = stretch?.dateLocal
    || stretches?.[0]?.dateLocal
    || isoDay(range?.start);
  const openInsights = React.useCallback(
    () => navigate(workSummaryInsightsTo(userId, insightsDay)),
    [navigate, userId, insightsDay],
  );
  const tracked = m?.trackedMinutes || 0;
  const idle = m?.idleMinutes || 0;
  const active = Math.max(0, tracked - idle);
  const activePct = tracked > 0 ? Math.round((active / tracked) * 100) : 0;
  const projects = dossier?.projects || [];

  const [hotId, setHotId] = React.useState(null);
  React.useEffect(() => { setHotId(null); }, [view, focusIndex, projectId]);

  const orgTz = worklog?.range?.timezone;
  const chapters = React.useMemo(() => groupChapters(stretches, orgTz), [stretches, orgTz]);
  const lookCount = stretches.filter((x) => stretchVerdict(x) === "look").length;
  const topProjects = React.useMemo(
    () => [...(dossier?.projects || [])].sort((a, b) => (b.trackedMinutes || 0) - (a.trackedMinutes || 0)).filter((p) => p.trackedMinutes > 0).slice(0, 2),
    [dossier],
  );
  const sentence = loading ? "Reading the day…" : (
    <>
      Tracked <b>{fmtMins(tracked)}</b>
      {periodLabel ? <> on <b>{periodLabel}</b></> : null}.
          {chapters.length > 0 ? (
        <>
          {" "}{chapters.length} tool{chapters.length === 1 ? "" : "s"}
          {topProjects.length === 2 ? <> — most of it on <b>{topProjects[0].name}</b> and <b>{topProjects[1].name}</b></>
            : topProjects.length === 1 ? <> — most of it on <b>{topProjects[0].name}</b></>
              : null}.
        </>
      ) : " No named threads in this window."}
      {lookCount === 1 ? " One sitting is still worth a look." : lookCount > 1 ? ` ${lookCount} sittings are still worth a look.` : null}
    </>
  );

  /* Segments are REAL fetched stretches only — start/end wall times mapped to
     minutes-since-midnight. Multi-day windows get no segments at all (a shared
     midnight axis would be a lie), so the instrument hides itself. */
  const singleDay = worklog?.range?.days === 1
    || (worklog?.range?.days == null && stretches.length > 0
      && new Set(stretches.map((s) => s.dateLocal)).size === 1);

  const { segs, inkForProject } = React.useMemo(() => {
    const inkMap = new Map();
    const inkFor = (nm) => {
      const key = nm || "—";
      if (!inkMap.has(key)) inkMap.set(key, INK_CYCLE[inkMap.size % INK_CYCLE.length]);
      return inkMap.get(key);
    };
    const out = [];
    (stretches || []).forEach((st, i) => {
      const ink = inkFor(st.project); // assign in list order, valid times or not
      if (!singleDay) return;
      const t0 = hmInView(st, "t0", orgTz);
      const t1 = hmInView(st, "t1", orgTz);
      const s = parseHM(t0);
      const e = parseHM(t1);
      if (s == null || e == null || e <= s) return;
      out.push({
        id: st.id || `st-${i}`,
        s, e, ink,
        index: i,
        projectKey: st.project || "—",
        label: `${st.title || "Tracked time"}${st.project ? ` — ${st.project}` : ""}, ${t0} to ${t1}`,
      });
    });
    (dossier?.projects || []).forEach((p) => inkFor(p.name)); // dots for rows with no stretch
    return { segs: out, inkForProject: inkFor };
  }, [stretches, singleDay, dossier, orgTz]);

  return (
    <DrillFrame
      key={`${view}-${focusIndex ?? ""}-${stretch?.id || ""}`}
      t={t}
      layer={layer}
      title={view === "project" ? projectName : view === "takeaway" ? (leadTitle(stretch?.title) || stretch?.app || "This sitting") : view === "log" ? "Full work log" : name}
      /* One head anatomy at every level: person → their avatar; project →
         the project glyph; takeaway → the stretch glyph; log → the book. */
      icon={view === "project"
        ? <FolderKanban size={22} strokeWidth={2.2} />
        : view === "takeaway"
          ? (stretch && knownTool(t, stretchApp(stretch))
            ? <ToolIcon name={stretchApp(stretch)} size={36} />
            : <Sparkles size={22} strokeWidth={2.2} />)
          : view === "log"
            ? <BookOpen size={22} strokeWidth={2.2} />
            : <Avatar user={dossier?.identity || null} name={name} size={50} />}
      tone={view === "project" ? "violet" : view === "log" ? "teal" : "blue"}
      eyebrow={view === "project" ? "Person · on a project" : view === "takeaway" ? "Person · one sitting" : view === "log" ? "Person · the written record" : "Person"}
      chips={view === "digest" || view === "takeaway" ? [] : [
        name,
        view === "project" && projectRow ? `${fmtMins(projectRow.trackedMinutes)} on it` : null,
        periodLabel,
      ]}
      sentence={view === "digest" ? sentence : view === "takeaway" && stretch ? (
        <>
          From <b>{hmInView(stretch, "t0", orgTz) || "—"}</b> to <b>{hmInView(stretch, "t1", orgTz) || "—"}</b>
          {periodLabel ? <> on <b>{periodLabel}</b></> : null}.
          {stretch.project ? <> On <b>{stretch.project}</b>.</> : " No project attributed."}
          {stretch.unclear ? " Not yet analysed." : null}
        </>
      ) : null}
      readings={view === "digest" ? [
        { icon: <Clock size={16} strokeWidth={2.2} />, value: loading ? "—" : fmtMins(tracked), label: periodLabel ? `tracked · ${periodLabel}` : "tracked", tone: "blue" },
        { icon: <Zap size={16} strokeWidth={2.2} />, value: fmtMins(active), label: `${activePct}% of tracked was active`, tone: "teal" },
        { icon: <Pause size={16} strokeWidth={2.2} />, value: fmtMins(idle), label: idle > 0 ? "idle in this window" : "no idle in this window", tone: "gold" },
      ] : view === "takeaway" && stretch ? [
        { icon: <Clock size={16} strokeWidth={2.2} />, value: stretch.durationLabel || "—", label: "this sitting", tone: "blue" },
        { icon: <Play size={16} strokeWidth={2.2} />, value: hmInView(stretch, "t0", orgTz) || "—", label: "started", tone: "teal" },
        { icon: <Pause size={16} strokeWidth={2.2} />, value: stretch.t1 || "—", label: "ended", tone: "gold" },
      ] : null}
      subtitle={[view === "digest" ? position : name, periodLabel].filter(Boolean).join(" · ")}
      crumbs={crumbs}
      layers={layers}
      backTo={backTo}
      backLabel={backLabel}
      subject={view === "digest" || view === "takeaway" ? [] : subject}
      excludes={excludes}
    >
      <DashAntd>
        {error ? (
          <GPanel t={t}><div style={{ padding: 28, color: t.c.textTertiary }}>{error}</div></GPanel>
        ) : view === "takeaway" ? (
          worklogLoading ? (
            <GPanel t={t} className="tgw-a tgw-a2"><SkeletonRows count={4} /></GPanel>
          ) : stretch ? (
            <StretchSolid
              t={t}
              stretch={stretch}
              index={focusIndex}
              total={stretches.length}
              onJump={openTakeaway}
              onFullLog={openInsights}
              userId={userId}
            />
          ) : (
            <GPanel t={t}><div style={{ padding: 24, color: t.c.textTertiary }}>That takeaway is not in this window. Open the full log.</div></GPanel>
          )
        ) : view === "project" ? (
          <MemberInProject
            member={{ userId, firstName: name, lastName: "", totalHours: (projectRow?.trackedMinutes || 0) / 60 }}
            project={{ id: projectId, name: projectName }}
            range={range}
            onBack={onBack}
            onOpenPerson={null}
          />
        ) : view === "log" ? (
          worklogLoading ? (
            <GPanel t={t} className="tgw-a tgw-a2">
              <PanelHead t={t} title="The written record" subtitle="every stretch in this window" info="attention" />
              <SkeletonRows count={6} />
            </GPanel>
          ) : hasWork ? (
            <GPanel t={t} className="tgw-a tgw-a2" style={{ overflow: "visible" }}>
              <PanelHead t={t} title="The written record" subtitle="every stretch on the clock · click one" info="attention" />
              {/* The day as a timeline (TimelineKit, mock-approved): time
                  gutter + inked node per stretch, the write-up as beats,
                  untracked gaps as dashed rows, multi-day grouped under day
                  dividers. hotIds stay the instrument's segment ids, so row ↔
                  timeline cross-light works exactly as before. */}
              <TimeRail
                t={t}
                rows={railFromStretches(stretches, {
                  inkFor: inkForProject,
                  idFor: (st, i) => {
                    const g = segs.find((x) => x.index === i);
                    return g ? g.id : `row-${i}`;
                  },
                })}
                hotId={hotId}
                onHot={setHotId}
                onOpen={(row) => openTakeaway(row.index)}
              />
            </GPanel>
          ) : (
            <WorkLogEmpty t={t} worklog={worklog} name={name} />
          )
        ) : (
          <div className="tgw-pair tgw-a tgw-a2" style={{
            display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "stretch",
          }}>
            <GPanel t={t} style={{
              margin: 0, display: "flex", flexDirection: "column", minHeight: 0,
              maxHeight: "calc(100vh - 268px)", overflow: "hidden",
            }}>
              <SectionBar t={t} tone="violet" icon={<Layers size={16} strokeWidth={2.2} />} title="The day, in order" info="focus"
                bits={[
                  { icon: <AppWindow size={12} strokeWidth={2.2} />, label: `${chapters.length || 0} tool${chapters.length === 1 ? "" : "s"}` },
                  { icon: <Clock size={12} strokeWidth={2.2} />, label: `${stretches.length} sitting${stretches.length === 1 ? "" : "s"}` },
                ]} />
              <div style={{ flex: 1, minHeight: 0, overflow: "auto", margin: "0 -6px", padding: "0 6px 4px" }}>
              {worklogLoading ? <SkeletonRows count={4} /> : hasWork ? (
                <Chapters t={t} stretches={stretches} onOpen={openTakeaway} timezone={orgTz} />
              ) : (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "12px 4px",
                  fontSize: 13, color: t.c.textTertiary,
                }}>
                  <Coffee size={15} strokeWidth={2.2} />
                  Nothing tracked in this window.
                </div>
              )}
              </div>
            </GPanel>

            <aside>
              {(worklog?.activity?.apps || []).length > 0 && (
                <GPanel t={t} style={{ marginBottom: 12 }}>
                  <SectionBar t={t} tone="blue" icon={<AppWindow size={16} strokeWidth={2.2} />} title="Where the hours went" info="focus"
                    bits={[{ icon: <AppWindow size={12} strokeWidth={2.2} />, label: `${worklog.activity.apps.length} tools` }]} />
                  <HoursWent t={t} apps={worklog.activity.apps} totalMinutes={worklog.activity.totalMinutes || tracked} />
                </GPanel>
              )}
              <GPanel t={t} style={{ margin: 0 }}>
                <SectionBar t={t} tone="gold" icon={<FolderKanban size={16} strokeWidth={2.2} />} title="Projects" info="projectShare"
                  bits={[{ icon: <FolderKanban size={12} strokeWidth={2.2} />, label: projects.length ? `${projects.length} project${projects.length === 1 ? "" : "s"}` : "none in this window" }]} />
                <ProjectRows t={t} projects={projects} inkFor={inkForProject} onOpen={openProject} />
                <div style={{ marginTop: 10 }}>
                  <LogDoor t={t} onClick={openInsights} icon={<BookOpen size={18} />}
                    title="Work summary · insights"
                    sub={name ? `${String(name).trim().split(/\s+/)[0]}’s day` : "That person’s day"} />
                </div>
              </GPanel>
            </aside>
          </div>
        )}
      </DashAntd>
    </DrillFrame>
  );
}

export default GraphicalPerson;
